import { useEffect, useSyncExternalStore } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

/* ============================================================
   PRAZOS DO SGI — contagem partilhada

   Os números vermelhos e âmbar do Sistema Integrado: o da barra lateral (só
   atrasados) e os de cada área e separador (atrasados, ou a vencer quando não
   há atrasados). Vêm todos de /sistema-integrado/prazos/resumo, que é a mesma
   conta do painel 🔔 Prazos — o número do menu nunca anuncia o que o painel
   não mostra.

   Não precisa de Provider: o resumo vive neste módulo e quem usa o hook lê
   sempre o mesmo. Mudar o aviso prévio no painel, ou voltar à janela depois de
   fechar uma NC, atualiza de uma vez a barra lateral, as áreas e os
   separadores — não há duas cópias do número a discordar.

   Sem polling, como os outros contadores da barra lateral: pede ao montar e
   quando o separador do navegador volta a ficar visível ou a janela ganha foco.
   Dois pedidos ao mesmo tempo (barra lateral e página a montar juntas, ou
   foco e visibilidade no mesmo instante) fazem uma só chamada.
   ============================================================ */

let estado = { resumo: null };
const ouvintes = new Set();
let emCurso = null;
let sequencia = 0;

const publicar = (novo) => {
    estado = { ...estado, ...novo };
    ouvintes.forEach(f => f());
};
const subscrever = (f) => { ouvintes.add(f); return () => ouvintes.delete(f); };
const ler = () => estado;

// forcar: pede de novo mesmo com um pedido a meio — depois de gravar o aviso
// prévio, a resposta que já vinha a caminho foi calculada com o valor antigo.
export const recarregarPrazosSgi = (forcar = false) => {
    if (emCurso && !forcar) return emCurso;
    const meu = ++sequencia;
    const pedido = api.get('/sistema-integrado/prazos/resumo')
        .then(r => { if (meu === sequencia) publicar({ resumo: r.data }); })
        .catch(() => {
            // silencioso de propósito: é um indicador acessório e nunca deve
            // interromper o que o utilizador está a fazer
        })
        .finally(() => { if (emCurso === pedido) emCurso = null; });
    emCurso = pedido;
    return pedido;
};

export const usePrazosSgi = () => {
    const { user, isAuthenticated, loading: authLoading, hasPermission } = useAuth();
    const { resumo } = useSyncExternalStore(subscrever, ler);

    // Sem sessão o interceptor trataria o 401 como sessão expirada e atirava
    // quem está numa página pública para o login. Os perfis de telemóvel não
    // têm o Sistema Integrado no menu — não se pede o que nunca se mostra.
    const podeConsultar = !authLoading && isAuthenticated && !user?.perfil_mobile
        && (typeof hasPermission !== 'function' || hasPermission('sistema-integrado'));

    useEffect(() => {
        if (!podeConsultar) return undefined;
        recarregarPrazosSgi();
        const aoVoltar = () => { if (document.visibilityState === 'visible') recarregarPrazosSgi(); };
        document.addEventListener('visibilitychange', aoVoltar);
        window.addEventListener('focus', aoVoltar);
        return () => {
            document.removeEventListener('visibilitychange', aoVoltar);
            window.removeEventListener('focus', aoVoltar);
        };
    }, [podeConsultar]);

    return { resumo: podeConsultar ? resumo : null, recarregar: recarregarPrazosSgi, ativo: podeConsultar };
};
